/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
'use client';

import { useEffect, useState, useRef, type ReactNode, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockPart } from '@akademi/site-builder-runtime';
import type { TabSpec } from './styles';

// CourseTabs — real tabs done properly.
//
// What "done properly" means here:
//
//   1. SSR-friendly. All panels render on the server (Ctrl+F and SEO
//      both work). Client only adds the hide/show behaviour after
//      hydration. Before hydration: every panel is visible (graceful
//      degradation for users with JS disabled).
//
//   2. Deep-linkable. `/learn/abc#reviews` opens the reviews tab
//      directly. Clicking a tab updates the URL hash. Browser back/
//      forward navigates between tabs.
//
//   3. Keyboard accessible. Arrow keys cycle, Home/End jump to first/
//      last. ARIA roles: tablist / tab / tabpanel with proper
//      aria-selected and aria-controls wiring.
//
//   4. The "active tab before hydration" problem is handled by reading
//      URL hash in useEffect (not useState initial value) — that's the
//      only way to make SSR + initial active tab work without a
//      hydration mismatch warning. The cost: a brief moment after
//      first paint where all panels are visible. We accept this in
//      exchange for SSR correctness.

export interface CourseTabsProps {
  /** Tab descriptors — id, label, optional `show: false` to hide. */
  tabs: TabSpec[];
  /** Rendered content for each tab id. Keys must match tab ids. */
  panels: Record<string, ReactNode>;
  /** Initial tab if no URL hash is present. Defaults to first visible. */
  defaultTabId?: string;
}

export function CourseTabs({ tabs, panels, defaultTabId }: CourseTabsProps) {
  // Aliased to `tr` — `t` is the per-tab loop variable throughout.
  const { t: tr } = useTranslation('common');
  const part = useBlockPart();
  // Filter once — never render hidden tabs at all.
  const visible = tabs.filter((t) => t.show !== false);

  // useState's initial value is read during SSR; we can't access
  // window.location.hash there. We start with the default and let
  // useEffect re-sync to the hash on the client.
  const initial = defaultTabId ?? visible[0]?.id ?? '';
  const [active, setActive] = useState<string>(initial);
  const [hydrated, setHydrated] = useState(false);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Hydrate: pick up the URL hash if it points at a known tab.
  useEffect(() => {
    setHydrated(true);
    const fromHash = window.location.hash.replace(/^#/, '');
    if (fromHash && visible.some((t) => t.id === fromHash)) {
      setActive(fromHash);
    }
    // Listen for back/forward navigation between hashes.
    const onHashChange = () => {
      const next = window.location.hash.replace(/^#/, '');
      if (next && visible.some((t) => t.id === next)) setActive(next);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Tab click — update state AND URL hash. We use replaceState here
  // (not pushState) for the click case so a long tab-clicking session
  // doesn't bury the user's previous page deep in browser history.
  // Deep-linked arrivals still create proper history entries because
  // the hashchange event fires for those.
  const selectTab = (id: string) => {
    setActive(id);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.hash = id;
      window.history.replaceState(null, '', url.toString());
    }
  };

  // Keyboard nav — arrow keys cycle, Home/End jump.
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const ids = visible.map((t) => t.id);
    const i = ids.indexOf(active);
    let next: string | undefined;
    if (e.key === 'ArrowRight') next = ids[(i + 1) % ids.length];
    else if (e.key === 'ArrowLeft') next = ids[(i - 1 + ids.length) % ids.length];
    else if (e.key === 'Home') next = ids[0];
    else if (e.key === 'End') next = ids[ids.length - 1];
    if (next) {
      e.preventDefault();
      selectTab(next);
      tabRefs.current[next]?.focus();
    }
  };

  if (visible.length === 0) return null;

  return (
    <div className="cd-tabs">
      <div
        role="tablist"
        aria-label={tr('label.courseSections')}
        className="cd-tablist"
        {...part('tablist')}
      >
        {visible.map((t, i) => {
          const selected = hydrated && t.id === active;
          return (
            <button
              key={t.id}
              ref={(el) => {
                tabRefs.current[t.id] = el;
              }}
              role="tab"
              id={`cd-tab-${t.id}`}
              aria-selected={selected}
              aria-controls={`cd-panel-${t.id}`}
              tabIndex={selected ? 0 : -1}
              className="cd-tab"
              onClick={() => selectTab(t.id)}
              onKeyDown={onKeyDown}
              {...part('tab', i)}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {visible.map((t, i) => {
        // Before hydration, all panels render visible (no `hidden`).
        // This is the SSR-correct path: the server renders without
        // knowing which tab the URL hash will point to, so it shows
        // everything. Once hydration runs, non-active panels get
        // hidden. The trade-off: brief flash of all-panels-visible
        // before hydration completes. Worth it for SSR correctness.
        const isHidden = hydrated && t.id !== active;
        return (
          <div
            key={t.id}
            role="tabpanel"
            id={`cd-panel-${t.id}`}
            aria-labelledby={`cd-tab-${t.id}`}
            className="cd-tabpanel"
            hidden={isHidden}
            {...part('panel', i)}
          >
            {panels[t.id]}
          </div>
        );
      })}
    </div>
  );
}
